import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Popover,
  Card,
  CardContent,
} from "@mui/material";
import { DateRange } from 'react-date-range';
import { CalendarToday as CalendarIcon, Message, Timer, Phone as PhoneIcon, Token, ChatSharp as ChatSharpIcon, AttachMoney as MoneyIcon } from '@mui/icons-material';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useDispatch, useSelector } from "react-redux";
import { getWebhookData } from "../redux/actions/billingActions";

function UserDashboard({ selectedUserId }) {
  const dispatch = useDispatch();
  const { webhookData, loading, error } = useSelector((s) => s.billing);

  const [dateRange, setDateRange] = useState([{ startDate: null, endDate: null, key: 'selection' }]);
  const [dateRangeAnchor, setDateRangeAnchor] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState('Select Date Range');

  // Guard against StrictMode double-mount: fetch only once per userId
  const lastFetchKeyRef = useRef("");
  useEffect(() => {
    if (!selectedUserId) return;
    const key = `${selectedUserId}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    dispatch(getWebhookData({ userId: selectedUserId }));
  }, [dispatch, selectedUserId]);

  const handleDateRangeClick = (event) => setDateRangeAnchor(event.currentTarget);
  const handleDateRangeClose = () => setDateRangeAnchor(null);

  const predefinedRanges = [
    { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
    { label: 'This Week', getValue: () => {
      const today = new Date();
      const start = new Date(today); start.setDate(today.getDate() - today.getDay());
      return { startDate: start, endDate: today };
    }},
    { label: 'This Month', getValue: () => {
      const today = new Date();
      return { startDate: new Date(today.getFullYear(), today.getMonth(), 1), endDate: today };
    }},
    { label: 'This Year', getValue: () => {
      const today = new Date();
      return { startDate: new Date(today.getFullYear(), 0, 1), endDate: today };
    }},
  ];

  const handlePredefinedRange = (range) => {
    const { startDate, endDate } = range.getValue();
    setDateRange([{ startDate, endDate, key: 'selection' }]);
    setSelectedDateRange(range.label);
    handleDateRangeClose();
  };

  const handleSearch = useCallback(() => {
    if (!selectedUserId) return;
    let startDate = '';
    let endDate = '';
    if (dateRange[0]?.startDate) {
      const s = new Date(dateRange[0].startDate);
      startDate = `${s.getFullYear()}-${String(s.getMonth()+1).padStart(2,'0')}-${String(s.getDate()).padStart(2,'0')}`;
    }
    if (dateRange[0]?.endDate) {
      const e = new Date(dateRange[0].endDate);
      endDate = `${e.getFullYear()}-${String(e.getMonth()+1).padStart(2,'0')}-${String(e.getDate()).padStart(2,'0')}`;
    }
    dispatch(getWebhookData({ userId: selectedUserId, startDate, endDate }));
  }, [dispatch, selectedUserId, dateRange]);

  const handleReset = () => {
    setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setSelectedDateRange('Select Date Range');
    if (selectedUserId) dispatch(getWebhookData({ userId: selectedUserId }));
  };

  const summary = useMemo(() => {
    const percredit = webhookData?.getCosting?.[0]?.percredit || 0;
    const results = webhookData?.processedData?.results?.[0] || {};
    const messages = results.total_messages || 0;
    const minutesSecs = results.total_call_duration_secs || 0;
    const calls = results.total_conversation || 0;
    const chatCredits = results.total_call_credits || 0;
    const llmCredits = results.lmmCredits || 0;
    const llmCost = webhookData?.processedData?.totalCharges_llm || 0;
    const chatCost = percredit * chatCredits;
    const totalCredits = chatCredits + llmCredits;
    const totalCost = chatCost + llmCost;
    const avgCostPerMsg = messages ? (chatCost / messages) : 0;
    const avgCostPerMin = minutesSecs ? (llmCost / (minutesSecs / 60)) : 0;
    const avgCostPerCall = calls ? (totalCost / calls) : 0;
    const avgCreditsPerCall = calls ? (totalCredits / calls) : 0;
    return {
      messages,
      minutesSecs,
      calls,
      chatCredits,
      llmCredits,
      percredit,
      chatCost,
      llmCost,
      totalCredits,
      totalCost,
      avgCostPerMsg,
      avgCostPerMin,
      avgCostPerCall,
      avgCreditsPerCall,
    };
  }, [webhookData]);

  const formatMinutes = (secs) => `${Math.round((secs||0)/60)} min`;
  const money = (v) => `$${(v||0).toFixed(4)}`;

  const metrics = useMemo(() => ([
    {
      label: "Messages",
      value: (summary.messages || 0).toLocaleString(),
      perValue: money(summary.avgCostPerMsg),
      perLabel: "Average cost per message",
      icon: Message,
      color: "#10b981",
    },
    {
      label: "Minutes",
      value: formatMinutes(summary.minutesSecs),
      perValue: money(summary.avgCostPerMin),
      perLabel: "Average cost per minute",
      icon: Timer,
      color: "#10b981",
    },
    {
      label: "Calls",
      value: (summary.calls || 0).toLocaleString(),
      perValue: money(summary.avgCostPerCall),
      perLabel: "Average cost per call",
      icon: PhoneIcon,
      color: "#10b981",
    },
    {
      label: "Chat Credits",
      value: (summary.chatCredits || 0).toLocaleString(),
      perValue: null,
      perLabel: null,
      icon: ChatSharpIcon,
      color: "#555",
    },
    {
      label: "LLM Credit",
      value: (summary.llmCredits || 0).toLocaleString(),
      perValue: null,
      perLabel: null,
      icon: Token,
      color: "#555",
    },
    {
      label: "Total Credits",
      value: (summary.totalCredits || 0).toLocaleString(),
      perValue: `${Math.round((summary.avgCreditsPerCall || 0))} Credits/Call`,
      perLabel: "Average Cost",
      icon: Token,
      color: "#10b981",
    },
    {
      label: "Chat Cost",
      value: `$${(summary.chatCost||0).toFixed(2)}`,
      perValue: null,
      perLabel: null,
      icon: MoneyIcon,
      color: "#ef4444",
    },
    {
      label: "LLM Cost",
      value: `$${(summary.llmCost||0).toFixed(2)}`,
      perValue: null,
      perLabel: null,
      icon: MoneyIcon,
      color: "#ef4444",
    },
    {
      label: "Total Cost",
      value: `$${(summary.totalCost||0).toFixed(2)}`,
      perValue: null,
      perLabel: null,
      icon: MoneyIcon,
      color: "#ef4444",
    },
  ]), [summary]);

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, width: '100%', minWidth: 0, mt: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
          User Management
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, xl: 2, lg: 3, md: 4 }}>
            <Button
              variant="outlined"
              onClick={handleDateRangeClick}
              startIcon={<CalendarIcon />}
              size="medium"
              fullWidth
              sx={{
                borderColor: '#e2e8f0',
                color: '#6b7280',
                backgroundColor: '#f8fafc',
                textTransform: 'none',
                justifyContent: 'space-between',
                borderRadius: '8px',
                '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#e2e8f0', color: '#374151' },
              }}
            >
              {selectedDateRange}
            </Button>
            <Popover open={Boolean(dateRangeAnchor)} anchorEl={dateRangeAnchor} onClose={handleDateRangeClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { p: 3, minWidth: 600, maxWidth: 800 } }}>
              <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#6b7280' }}>Quick Select</Typography>
                  {predefinedRanges.map((range) => (
                    <Button key={range.label} onClick={() => handlePredefinedRange(range)} size="small" sx={{ display:'block', width:'100%', textTransform:'none', mb: 0.5 }}>
                      {range.label}
                    </Button>
                  ))}
                </Box>
                <Box>
                  <DateRange editableDateInputs onChange={(item)=> item?.selection && setDateRange([item.selection])} moveRangeOnFirstSelection={false} ranges={dateRange} showDateDisplay={false} months={1} direction="horizontal" />
                </Box>
              </Box>
              <Box sx={{ display:'flex', justifyContent:'flex-end', gap:1, pt:2 }}>
                <Button variant="outlined" onClick={handleDateRangeClose} size="small">Cancel</Button>
                <Button variant="contained" onClick={()=>{ // set label
                  if (dateRange[0]?.startDate && dateRange[0]?.endDate) {
                    const s = new Date(dateRange[0].startDate); const e = new Date(dateRange[0].endDate);
                    const sStr = s.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
                    const eStr = e.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
                    setSelectedDateRange(`${sStr} - ${eStr}`);
                  } else setSelectedDateRange('Select Date Range');
                  handleDateRangeClose();
                }} size="small">Save</Button>
              </Box>
            </Popover>
          </Grid>
          <Grid display="flex" gap={1} size={{ xs: 12, lg: 3, md: 4 }}>
            <Button variant="contained" onClick={handleSearch} size="medium" fullWidth sx={{ bgcolor:'#111827', '&:hover':{bgcolor:'#0b1220'}, textTransform:'none' }}>Search</Button>
            <Button variant="outlined" onClick={handleReset} size="medium" fullWidth sx={{ borderColor:'#111827', color:'#111827', textTransform:'none', '&:hover':{ borderColor:'#0b1220', backgroundColor:'#f9fafb' }}}>Reset</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Metrics - Mirror full dashboard layout */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ overflowX: 'auto', "&::-webkit-scrollbar": { height: '6px' } }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {metrics.map((metric, index) => {
              const IconComponent = metric.icon;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                  <Card sx={{ height: '100%', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'none', backgroundColor: '#ffffff', '&:hover': { borderColor: '#d1d5db', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' } }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <IconComponent sx={{ fontSize: { xs: 24, sm: 28 }, color: metric.color }} />
                      </Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontSize: { xs: '11px', sm: '12px', md: '13px' }, mb: { xs: 0.75, sm: 1 }, textAlign: 'center' }}>
                        {metric.label}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                        {loading ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem' } }}>
                            <CircularProgress size={20} sx={{ color: metric.color }} />
                          </Box>
                        ) : (
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}>
                            {metric.value}
                          </Typography>
                        )}
                        {metric.perValue && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            {loading ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '1rem' }}>
                                <CircularProgress size={12} sx={{ color: metric.color }} />
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: metric.color, fontSize: { xs: '10px', sm: '11px', md: '12px' } }}>
                                {metric.perValue}
                              </Typography>
                            )}
                            {metric.perLabel && (
                              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: { xs: '9px', sm: '10px' }, textAlign: 'center' }}>
                                {metric.perLabel}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}

export default UserDashboard;


